import React from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';

const SummaryCard = ({
  title,
  value,
  icon: Icon,
  subtitle,
  trend,
  trendLabel,
  loading = false,
  layout = 'column'
}) => {
  // Shared base container styles
  const containerClasses = "group relative bg-white dark:bg-[#002147] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden";
  
  // Shared icon container styles (Deep Navy / White Icon)
  const iconContainerClasses = "w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300 shrink-0 text-white";

  // Value rendering with loading skeleton
  const renderValue = () => {
    if (loading) {
      return <span className="inline-block w-16 h-8 bg-gray-200 dark:bg-gray-700 animate-pulse rounded" />;
    }
    return value;
  };

  if (layout === 'row') {
    return (
      <div className={`${containerClasses} flex items-center justify-between gap-3`}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#00152E] to-[#1a3884] opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
        <div className="relative flex items-center justify-between gap-3 w-full">
          <div className="space-y-1 min-w-0 flex-1">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide block leading-tight" title={title}>
              {title}
            </p>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
              {renderValue()}
            </h2>
          </div>
          {Icon && (
            <div className={iconContainerClasses}>
              <Icon size={18} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default 'column' layout (used in Admin & Recruiter Dashboards)
  const isTrendNegative = trend && trend.toString().includes('-');

  return (
    <div className={containerClasses}>
      <div className="absolute inset-0 bg-gradient-to-br from-[#00152E] to-[#1a3884] opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <div className="flex-1">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
              {title}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
            )}
          </div>
          {Icon && (
            <div className={iconContainerClasses}>
              <Icon size={18} />
            </div>
          )}
        </div>
        
        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
          {renderValue()}
        </div>

        {(trend || trendLabel) && (
          <div className="flex items-center justify-between mt-1">
            {trend && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#1a3884]/8 dark:bg-[#1a3884]/30">
                {isTrendNegative ? (
                  <ArrowDown size={10} className="text-red-500 dark:text-red-400" />
                ) : (
                  <ArrowUp size={10} className="text-[#1a3884] dark:text-[#4f7dff]" />
                )}
                <span className={`text-[10px] font-bold ${isTrendNegative ? 'text-red-500 dark:text-red-400' : 'text-[#1a3884] dark:text-[#4f7dff]'}`}>
                  {trend}
                </span>
              </div>
            )}
            {trendLabel && (
              <span className="text-[10px] text-gray-500">{trendLabel}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryCard;
